// @ts-check
/**
 * The company's GST registration details. GST tax invoices (see the Tax Invoice card on Order
 * Detail) are not generated for any order until every field here is filled in.
 */
import { AlertTriangle, CheckCircle, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import { useToast } from '../context/ToastContext';
import adminGstinLookupService from '../services/adminGstinLookupService';
import adminSiteSettingsService from '../services/adminSiteSettingsService';
import { BILLING_KEYS, billingFormFromSettings, isBillingConfigured } from '../utils/billingSettings';
import { GST_STATE_NAMES } from '../utils/gstStates';

const input = 'w-full px-3 py-2 text-base border border-input rounded-lg';
const label = 'block text-sm font-medium mb-1';

const BillingSettingsPage = () => {
  const toast = useToast();
  const [form, setForm] = useState({ gstin: '', legalName: '', address: '', state: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);

  useEffect(() => {
    adminSiteSettingsService
      .getAllSettings()
      .then((settings) => setForm(billingFormFromSettings(settings)))
      .catch((err) => toast.error(err.message || 'Failed to load billing settings'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleLookup = async () => {
    const gstin = form.gstin.trim();
    if (!gstin) {
      toast.error('Enter a GSTIN first');
      return;
    }
    setLookingUp(true);
    try {
      const details = await adminGstinLookupService.lookup(gstin);
      setForm((prev) => ({ ...prev, legalName: details.legal_name, address: details.address, state: details.state }));
      toast.success(`Found: ${details.legal_name} (${details.status})`);
    } catch (err) {
      toast.error(err.message || 'GSTIN lookup failed');
    } finally {
      setLookingUp(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminSiteSettingsService.bulkUpdate(
        Object.fromEntries(Object.entries(BILLING_KEYS).map(([field, key]) => [key, form[field].trim()])),
      );
      toast.success('Billing settings saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save billing settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingScreen />;

  const configured = isBillingConfigured(form);

  return (
    <div className="max-w-xl">
      <h1 className="text-3xl font-serif mb-6">Billing</h1>

      <div
        className={`flex items-center gap-2 mb-6 px-4 py-3 rounded-lg text-sm ${
          configured
            ? 'bg-green-50 dark:bg-green-950/50 text-green-800 dark:text-green-300 border rounded-lg border-green-200 dark:border-green-900'
            : 'bg-amber-50 dark:bg-amber-950/50 text-amber-800 dark:text-amber-300 border rounded-lg border-amber-200 dark:border-amber-900'
        }`}
      >
        {configured ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
        {configured
          ? 'Tax invoices will be generated automatically on paid orders.'
          : 'Tax invoices are switched off until every field below is filled in.'}
      </div>

      <div className="space-y-4">
        <div>
          <label className={label}>GSTIN</label>
          <div className="flex gap-2">
            <input
              className={input}
              value={form.gstin}
              onChange={(e) => set('gstin', e.target.value)}
              placeholder="08ABCDE1234F1Z5"
            />
            <button
              onClick={handleLookup}
              disabled={lookingUp}
              className="shrink-0 inline-flex items-center gap-1 px-3 py-2 text-sm border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50"
            >
              <Search size={14} /> {lookingUp ? 'Looking up...' : 'Look up'}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Fills the fields below from the GST registration for this GSTIN. Check them before saving.
          </p>
        </div>
        <div>
          <label className={label}>Legal entity name</label>
          <input
            className={input}
            value={form.legalName}
            onChange={(e) => set('legalName', e.target.value)}
            placeholder="KAAY Fashion Pvt Ltd"
          />
        </div>
        <div>
          <label className={label}>Registered address</label>
          <textarea className={input} rows={3} value={form.address} onChange={(e) => set('address', e.target.value)} />
        </div>
        <div>
          <label className={label}>Registered state</label>
          <select className={input} value={form.state} onChange={(e) => set('state', e.target.value)}>
            <option value="">Choose the state...</option>
            {GST_STATE_NAMES.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            Must match a sale's state for it to charge CGST+SGST instead of IGST.
          </p>
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="mt-6 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:bg-muted-foreground/50"
      >
        {saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
};

export default BillingSettingsPage;
